// components/PostDetail.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Stack,
  Paper,
  Divider,
  Grid,
} from "@mui/material";
import { useParams } from "react-router-dom";

function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const postRes = await fetch(
          `/api/post/by-url/?url=${encodeURIComponent(slug)}`
        );
        if (!postRes.ok) throw new Error("Fehler beim Laden des Beitrags");
        const postData = await postRes.json();
        const loadedPost = Array.isArray(postData) ? postData[0] : postData;
        if (!loadedPost) throw new Error("Beitrag nicht gefunden");
        setPost(loadedPost);

        const [catRes, tagRes, attrRes, attrTypeRes] = await Promise.all([
          fetch("/api/category/"),
          fetch("/api/tag/"),
          fetch("/api/attribute/"),
          fetch("/api/attributetype/"),
        ]);

        const [catData, tagData, attrData, attrTypeData] = await Promise.all([
          catRes.json(),
          tagRes.json(),
          attrRes.json(),
          attrTypeRes.json(),
        ]);

        setCategories(catData);
        setTags(tagData);
        setAttributes(attrData);
        setAttributeTypes(attrTypeData);
      } catch (err) {
        setError(err.message || "Unbekannter Fehler");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>Beitrag nicht gefunden.</Typography>
      </Container>
    );
  }

  // Category (post.Category ist ID)
  const category =
    post.Category &&
    categories.find((c) => String(c.id) === String(post.Category));

  // ----- TAG-LOGIK ROBUST -----
  // Unterstützt:
  // - post.Tags = [1,2,3]
  // - post.Tags = [{ id, tag }]
  // - post.tags = ...
  const rawTagsField = post.Tags ?? post.tags ?? [];

  let postTagIds = [];
  if (Array.isArray(rawTagsField)) {
    if (rawTagsField.length > 0 && typeof rawTagsField[0] === "object") {
      // [{ id, tag }]
      postTagIds = rawTagsField
        .map((t) => t && t.id)
        .filter((id) => id !== undefined && id !== null);
    } else {
      // [1,2,3]
      postTagIds = rawTagsField;
    }
  }

  const postTags = tags.filter((t) =>
    postTagIds.map(String).includes(String(t.id))
  );

  // Maps für Attribute + Typen
  const attributeMap = Object.fromEntries(
    attributes.map((a) => [String(a.id), a])
  );
  const attributeTypeMap = Object.fromEntries(
    attributeTypes.map((t) => [String(t.id), t])
  );

  // PostAttributes: Objekte wie { id, PostAttribute, PostAttributeType, value }
  const enrichedPostAttrs = (
    Array.isArray(post.PostAttributes) ? post.PostAttributes : []
  ).map((rel) => ({
    id: rel.id,
    typeName:
      attributeTypeMap[String(rel.PostAttributeType)]?.postAttributeType || "",
    attributeName: attributeMap[String(rel.PostAttribute)]?.postAttribute || "",
    value: rel.value,
  }));

  const createdAtLabel = post.created_at
    ? new Date(post.created_at).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="App">
      <main>
        <Container maxWidth="md" sx={{ mt: 6, mb: 8, pt: { xs: 10, md: 12 } }}>
          {/* HEADER */}
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontWeight: 700,
              letterSpacing: "-0.5px",
            }}
          >
            {post.title}
          </Typography>

          {postTags.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {postTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.tag}
                  sx={{
                    backgroundColor: "#e8f0fe",
                    color: "#1a73e8",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#d2e3fc",
                    },
                  }}
                />
              ))}
            </Box>
          )}

          <Box className="articleThumbBox" sx={{ mb: 2 }}>
            <Typography sx={{ mb: 2 }}>Entscheidungen</Typography>
            <Box className="articleThumbGrid">
              {post.PostAttributes.map((attr) => (
                <>
                  <Box className="articleThumbGridItem">
                    <Typography>{attr.postAttribute}</Typography>
                    <Typography>{attr.value}</Typography>
                  </Box>
                </>
              ))}
            </Box>
          </Box>

          {/*
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 5,
              borderRadius: 4,
              background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                mb: 1.5,
              }}
            >
              {category && (
                <Chip
                  label={category.category}
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                />
              )}
              {createdAtLabel && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {createdAtLabel}
                </Typography>
              )}
            </Box>

            <Typography
              variant="h3"
              sx={{
                mb: 2,
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              {post.title}
            </Typography>


            {postTags.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {postTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.tag}
                    sx={{
                      backgroundColor: "#e8f0fe",
                      color: "#1a73e8",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "#d2e3fc",
                      },
                    }}
                  />
                ))}
              </Box>
            )}


            {enrichedPostAttrs.length > 0 && (
              <Box
                sx={{
                  mt: 4,
                  backgroundColor: "#f9fafb",
                  borderRadius: 3,
                  p: 2.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    opacity: 0.85,
                  }}
                >
                  Entscheidung im Überblick
                </Typography>
                <Stack spacing={1.2}>
                  {enrichedPostAttrs.map((attr) => (
                    <Box
                      key={attr.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        columnGap: 1.5,
                        rowGap: 0.5,
                      }}
                    >
                      {attr.typeName && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {attr.typeName}:
                        </Typography>
                      )}
                      {attr.attributeName && (
                        <Chip
                          size="small"
                          label={attr.attributeName}
                          sx={{
                            fontSize: "0.75rem",
                            bgcolor: "#eef2f7",
                          }}
                        />
                      )}
                      {attr.value && (
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {attr.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
           */}

          <Typography
            variant="body1"
            component="div"
            sx={{
              lineHeight: 1.8,
              whiteSpace: "pre-line",
              fontSize: "1.05rem",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: post.body }} />
          </Typography>
        </Container>
      </main>
    </div>
  );
}

export default PostDetail;
